import { COMPLAINT_STATUS } from "./constants";
import { isRichTextEmpty, stripHtml } from "./mapCtripEvents";

// export const generateVendorEventId = () => {
//   if (typeof crypto !== "undefined" && crypto.randomUUID) {
//     return crypto.randomUUID().replace(/-/g, "");
//   }
//   return `${Date.now()}${Math.random().toString(36).slice(2, 11)}`;
// };

export const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const FILE_CONTENT_TYPE_MAP = {
  xlsx: "application/vnd.ms-excel",
  xls: "application/vnd.ms-excel",
  jpeg: "image/jpeg",
  jpg: "image/jpeg",
  png: "image/png",
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/msword",
  pptx: "application/vnd.ms-powerpoint",
  rar: "application/x-rar",
  zip: "application/zip",
  tar: "application/tar",
  txt: "text/plain",
  csv: "text/csv",
  xml: "application/xml",
  msg: "application/msg",
};

const getFileExtension = (filename) => {
  const parts = String(filename || "").split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "";
};

export const getUploadContentType = (filename) => {
  const extension = getFileExtension(filename);
  return FILE_CONTENT_TYPE_MAP[extension] || "application/octet-stream";
};

export const buildUploadFileCtripPayload = (file, base64) => ({
  filename: file.name,
  file: base64,
  contentType: getUploadContentType(file.name),
  public: 1,
  oversea: 1,
});

export const buildEnclosureFromUpload = (uploadResponse, filename) => ({
  fileType: getFileExtension(filename),
  fileName: uploadResponse.file_name || filename,
  fileUrl: uploadResponse.url,
});

export const buildCustomerServiceReplyBody = (
  complaint,
  { content, enclosure, eventTypeId },
  operator
) => {
  const body = {
    pushType: 2,
    content,
    singleCallBack: true,
    operator,
    eventTypeId:
      eventTypeId ?? complaint?.eventDetail?.eventTypeId ?? undefined,
  };

  if (enclosure?.length) {
    body.enclosure = enclosure;
  }

  return body;
};

export const buildPenaltyReplyBody = (
  complaint,
  { action, comments, enclosure, eventTypeId },
  operator
) => {
  const isAccept = action === "accept";
  const body = {
    pushType: 2,
    content:
      (comments && !isRichTextEmpty(comments) ? comments : null) ||
      (isAccept ? "Penalty accepted" : "Appeal submitted"),
    singleCallBack: false,
    operator,
    eventTypeId:
      eventTypeId ?? complaint?.eventDetail?.eventTypeId ?? undefined,
    appealOperate: isAccept ? "CONFIRM" : "APPEAL",
  };

  if (enclosure?.length) {
    body.enclosure = enclosure;
  }

  return body;
};

export const isThunkError = (response) =>
  Boolean(response?.response || response?.isAxiosError);

export const getThunkErrorMessage = (response, fallback) =>
  response?.response?.data?.message ||
  response?.message ||
  fallback;

export const applyReplyToComplaint = (complaint, replyContent, eventSource, appealOperate) => {
  const now = new Date();
  const time = now.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });

  let status = complaint.status;
  if (eventSource === 2) {
    status =
      appealOperate === "CONFIRM"
        ? COMPLAINT_STATUS.PENALTY_CONFIRMED
        : COMPLAINT_STATUS.APPEALING;
  } else {
    status = COMPLAINT_STATUS.REPLIED;
  }

  const updated = {
    ...complaint,
    status,
    hasReply: true,
    rawStatus: eventSource === 2
      ? appealOperate === "CONFIRM"
        ? "penalty_confirmed"
        : "appealing"
      : "closed",
    timeline: [
      ...complaint.timeline,
      {
        id: `t-${Date.now()}`,
        time,
        actor: "Vendor",
        message: stripHtml(replyContent) || replyContent,
      },
    ],
  };

  if (eventSource === 2 && complaint.penalty) {
    updated.penalty = {
      ...complaint.penalty,
      currentStatus: status,
      hasPendingAppeal: appealOperate === "APPEAL",
      isConfirmed: appealOperate === "CONFIRM",
      appealable: appealOperate !== "CONFIRM" && complaint.penalty.appealable,
      remainingAppeals:
        appealOperate === "APPEAL"
          ? Math.max(0, (complaint.penalty.remainingAppeals || 0) - 1)
          : appealOperate === "CONFIRM"
            ? 0
            : complaint.penalty.remainingAppeals,
      appealStatus: appealOperate === "CONFIRM" ? "confirmed" : complaint.penalty.appealStatus,
      appealResult: appealOperate === "CONFIRM" ? "confirmed" : complaint.penalty.appealResult,
    };

    if (appealOperate === "APPEAL") {
      updated.appealHistory = [
        ...(complaint.appealHistory || []),
        {
          id: (complaint.appealHistory?.length || 0) + 1,
          label: `Appeal #${(complaint.appealHistory?.length || 0) + 1}`,
          status: "Pending",
        },
      ];
    }
  }

  return updated;
};

export const uploadCtripFiles = async (dispatch, uploadFileCtripThunk, files = []) => {
  if (!files.length) return [];

  const enclosures = [];

  for (const file of files) {
    const base64 = await fileToBase64(file);
    const payload = buildUploadFileCtripPayload(file, base64);
    const response = await dispatch(uploadFileCtripThunk(payload));

    if (isThunkError(response)) {
      throw new Error(
        getThunkErrorMessage(response, `Failed to upload ${file.name}`)
      );
    }

    if (!response?.url) {
      throw new Error(response?.message || `Failed to upload ${file.name}`);
    }

    enclosures.push(buildEnclosureFromUpload(response, file.name));
  }

  return enclosures;
};
